import { useState } from "react";
import { HelpIcon, Button, Skeleton } from '../../core/index.js'
import { Send } from 'lucide-react'
import type { ScoreInput } from "../../scoring/index.js";
import { useSubmitBarrier, useWorkerBarriers } from "../index.js";
import { ApiError, isNotFoundError } from "../../core/index.js";
import { BarrierList } from "./BarrierList.js";
import { LoadFailure } from '../../core/index.js'

const COMMENT_MAX_LENGTH = 1000;
const PERFECT_VALUE = 100;

/** The same test the API applies: counted, and short of 100. */
export function isBelowMetric(input: ScoreInput): boolean {
  if (input.included === false) {
    return false;
  }
  if (input.pointsLost !== null && input.pointsLost !== undefined) {
    return input.pointsLost > 0;
  }
  return input.value < PERFECT_VALUE;
}

/** The choice that makes a note a general one, about no behavior. */
const GENERAL = "general";

/**
 * Where a worker says what is getting in the way: in general, at any time
 * (the demo's "Tell us what's getting in the way"), or about one of the
 * behaviors costing them points this month - the ones the bars above show
 * short - so the facility reads it in the worker's own words beside the
 * number it was about. Their earlier notes follow, with whether the
 * facility has seen each.
 */
export function WorkerBarriers({
  workerId,
  inputs,
}: {
  workerId: string;
  inputs: readonly ScoreInput[];
}) {
  const below = inputs.filter(isBelowMetric);
  const barriersQuery = useWorkerBarriers(workerId);
  // The worker's own profile loaded, so a 404 here is an API deployed before barriers existed: nothing to offer yet.
  const notAvailable = isNotFoundError(barriersQuery.error);
  const submit = useSubmitBarrier(workerId);
  const [behaviorKey, setBehaviorKey] = useState<string>(GENERAL);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);

  // A behavior no longer short falls back to a general note rather than to another behavior.
  const chosen = below.find((input) => input.key === behaviorKey) ?? null;
  const canSend =
    comment.trim().length > 0 &&
    comment.length <= COMMENT_MAX_LENGTH &&
    !submit.isPending;
  const error =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.isError
        ? "Your note could not be sent. Try again in a moment."
        : null;

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSend) {
      return;
    }
    submit.mutate(
      { behaviorKey: chosen?.key ?? null, comment: comment.trim() },
      {
        onSuccess: () => {
          setComment("");
          setSent(true);
        },
      },
    );
  }

  return (
    <div className="bg-white border border-marine/15 p-4 flex flex-col gap-3">
      <div>
        <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          What’s getting in the way?
          <HelpIcon topic="messages.worker-barriers" />
        </div>
        <p className="text-[11px] text-ink-mid leading-snug mt-1">
          {below.length === 0
            ? "Anything at all: your facility reads it in your words and can look at what it needs to change. When a bar above is short, you can tie a note to it here too."
            : "In general, or about a short bar above. Your facility reads this in your words, beside the number where there is one, and can look at what it needs to change."}
        </p>
      </div>

      {notAvailable && (
        <p className="text-[11px] text-ink-mid">Not available yet.</p>
      )}

      {!notAvailable && (
        <form onSubmit={onSubmit} className="flex flex-col gap-2">
          <label className="text-[10px] font-bold text-ink">
            About
            <select
              value={chosen?.key ?? GENERAL}
              onChange={(event) => {
                setBehaviorKey(event.target.value);
                setSent(false);
              }}
              className="mt-1 w-full border border-marine/30 bg-white px-2 py-1.5 text-[12px] font-normal text-ink"
            >
              <option value={GENERAL}>In general · not about one behavior</option>
              {below.map((input) => (
                <option key={input.key} value={input.key}>
                  {input.label} · {Math.round(input.value)} of 100
                </option>
              ))}
            </select>
          </label>
          <label className="text-[10px] font-bold text-ink">
            What is in the way
            <textarea
              value={comment}
              maxLength={COMMENT_MAX_LENGTH}
              rows={3}
              onChange={(event) => {
                setComment(event.target.value);
                setSent(false);
              }}
              placeholder="In your own words. Nobody else is named or compared."
              className="mt-1 w-full border border-marine/30 bg-white px-2 py-1.5 text-[12px] font-normal text-ink leading-snug"
            />
          </label>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] text-ink-mid">
              {comment.length}/{COMMENT_MAX_LENGTH}
            </span>
            <Button icon={Send} size="sm" type="submit" disabled={!canSend}>
              {submit.isPending ? "Sending…" : "Send to my facility"}
            </Button>
          </div>
          {error && <div className="text-[11px] text-coral">{error}</div>}
          {sent && !submit.isPending && (
            <div className="text-[11px] text-green">
              Sent. Your facility will see it with your score.
            </div>
          )}
        </form>
      )}

      {!notAvailable && (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">
            What you’ve said
          </div>
          {barriersQuery.isPending ? (
            <Skeleton />
          ) : barriersQuery.isError ? (
            <LoadFailure size="xs" message="Your notes could not be loaded." onRetry={() => void barriersQuery.refetch()} retrying={barriersQuery.isFetching} />
          ) : (
            <BarrierList
              barriers={barriersQuery.data}
              audience="worker"
              empty="Nothing yet."
            />
          )}
        </div>
      )}
    </div>
  );
}
